<template lang="pug">
	div(style="min-height: 400px;")
		v-row.d-flex.justify-space-between
			v-col.d-flex.justify-start
				v-btn.mb-4(color="primary" small outlined ripple @click="displayDetailsView()")
					v-icon mdi-arrow-left
					| Appointments

		.text-subtitle-2.subtitle-2-padding.text-center
			h5 Find Available Appointments {{ isLoading || isIntegrationResourceLoading ? 'Loading...' : '' }}
				div.mt-4#picker-wrapper
					.reset-cover
						.reset-details
							.text-center
								p.text-small.mb-0 Dates:
								p.text-small {{ dateRange[0] }} - {{ dateRange[1] }}
							.text-center(v-if="formIntegrationAppointmentSlots.shopSid")
								p.text-small.mb-0 Shop:
								p.text-small {{ integrationShops.find(shop => shop.sid === formIntegrationAppointmentSlots.shopSid)?.name }}
							.text-center(v-if="formIntegrationAppointmentSlots.transportationTypeSid")
								p.text-small.mb-0 Transportation:
								p.text-small {{ integrationTransportationTypes.find(transportation => transportation.sid === formIntegrationAppointmentSlots.transportationTypeSid)?.name }}
							.text-center(v-if="formIntegrationAppointmentSlots.serviceAdvisorSid")
								p.text-small.mb-0 Service Advisor:
								p.text-small {{ integrationServiceAdvisors.find(serviceAdvisor => serviceAdvisor.sid === formIntegrationAppointmentSlots.serviceAdvisorSid)?.display_name }}
							v-btn(
								color='white'
								@click='resetForm'
								outlined
							)
								| Retry search

					div#date-selector
						template(v-if="isIntegrationResourceLoading")
							v-progress-circular(
								:size='100'
								:width='10'
								color='var(--volie-blue)'
								indeterminate
								style="margin-top: 100px;"
							)
						template(v-if="!isIntegrationResourceLoading && (validIntegrationFormResourceFields || showChangeCriteriaAlert)")
							v-row(justify="center")
								v-col(cols='9')
									.date-picker-container(:class="{ 'disabled': disableDatePicker }")
										v-date-picker(
											v-model='dateRange'
											full-width
											label='Select Date Range'
											range
											required
											multiple
											elevation="5"
											:min='minimumDate'
											:rules="[rules.required]"
											:selected-items-text="dateRangeText"
										)

								v-row(v-if="showVehicleFormBeforeAppointmentDateSelection")
									v-col(cols='12')
										VehicleCreate(
											:current-campaign-customer="currentCampaignCustomer"
											:customer-vehicles="customerVehicles"
											:handle-add-customer-vehicle="handleAddCustomerVehicle"
											:selected-vehicle="selectedVehicle"
											:set-selected-vehicle="setSelectedVehicle"
											:show-snackbar="showSnackbar"
											:handle-toggle-vehicle-form="handleToggleVehicleForm"
											:show-vehicle-form="showVehicleForm"
											:first-customer-vehicle="firstCustomerVehicle"
										)

								v-row(v-if="showServiceShop")
									v-col(cols='12')
										v-autocomplete(
											v-model="formIntegrationAppointmentSlots.shopSid"
												:items="integrationShops"
												item-text="name"
												item-value="sid"
												:label="serviceShopLabel"
												outlined
												dense
												:required="isServiceShopRequired"
												:rules="isServiceShopRequired ? [rules.required] : []"
												clearable
										)

								v-row(v-if="showTransportationType || showServiceAdvisor")
									v-col(:cols="showTransportationType && showServiceAdvisor ? '6' : '12'")
										v-autocomplete(
											v-if="showTransportationType"
											v-model="formIntegrationAppointmentSlots.transportationTypeSid"
											:items="integrationTransportationTypes"
											item-text="name"
											item-value="sid"
											:label="transportationTypeLabel"
											outlined
											dense
											:required="isTransportationTypeRequired"
											:rules="isTransportationTypeRequired ? [rules.required] : []"
											clearable
										)
									v-col(v-if="showServiceAdvisor" :cols="showTransportationType && showServiceAdvisor ? '6' : '12'")
										v-autocomplete(
											v-if="currentIntegration !== 'xtime'"
											v-model='formIntegrationAppointmentSlots.serviceAdvisorSid'
											:items="integrationServiceAdvisors"
											item-text='name'
											item-value='sid'
											:label="serviceAdvisorLabel"
											outlined
											dense
											:required="isServiceAdvisorRequired"
											:rules="isServiceAdvisorRequired ? [rules.required] : []"
											clearable
										)
										v-text-field(
											v-else
											outlined
											dense
											placeholder='Any Advisor'
											disabled
										)

								v-row(v-if="showServiceMenu")
									v-col(:cols="showOpcodeSelection ? '6' : '12'")
										v-autocomplete(
											v-model="formIntegrationAppointmentSlots.selectedService"
											:items="availableServices"
											item-text="serviceName"
											item-value="serviceName"
											label="Select Service"
											outlined
											dense
											required
											:rules="[rules.required]"
											clearable
											@change="onServiceChange"
										)
									v-col(cols='6' v-if="showOpcodeSelection")
										v-autocomplete(
											v-model="formIntegrationAppointmentSlots.opcode"
											:items="availableOpcodes"
											item-text="opcode"
											item-value="opcode"
											label="Select Opcode"
											outlined
											dense
											required
											:rules="[rules.required]"
											clearable
										)

								v-col(cols='9' v-if="!showChangeCriteriaAlert")
									v-btn(
										color='primary'
										:disabled='disabledFind'
										block
										@click='getAvailableSlots'
									)
										| Find Time Slots

						template(v-if="!showVehicleForm && !isIntegrationResourceLoading && !validIntegrationFormResourceFields && !showChangeCriteriaAlert")
							v-alert(outlined type='warning' dense border='left')
								| Something went wrong. Please refresh.
						template(v-else-if="!showVehicleForm && !isIntegrationResourceLoading && showChangeCriteriaAlert")
							v-alert(outlined type='warning' dense border='left')
								| Please change your search criteria.

					div#slot-picker
						div(v-if="!isLoading && !showNotFound" class="slots-list scrollable-panel")
							div(v-for="(slot, slotIndex) in integrationAppointmentSlots" :key="slotIndex")
								v-subheader.d-flex.justify-center.font-weight-bold {{ formatSubheaderDate(slot.appointmentDate) }}
								template(v-if="validSlotTimes(slot)")
									div.slot-item(v-for="(slot, timeIndex) in slot.times" :key="timeIndex" @click="selectSlot(slot)")
										p Time:
										p {{ slot.friendlyDateTime }}
								template(v-else)
									div.slot-item.unavailable
										p No slots available

						div(v-if="!isLoading && showNotFound")
							v-alert(outlined type='warning' dense border='left')
								| No available slots found please adjust your search criteria

						div(v-if="isLoading" class="text-center")
							v-progress-circular(
								:size='100'
								:width='10'
								color='var(--volie-blue)'
								indeterminate
								style="margin-top: 100px;"
							)

</template>

<script lang="ts">
import Vue from 'vue';

// Components
import VehicleCreate from '@/components/communication_center/shared/tabs/integration/vehicle_create.vue';

// Data
import { GetIntegrationAppointmentSlots } from "@/data";

export default Vue.extend({
	data() {
		return {
			formIntegrationAppointmentSlots: {
				shopSid: '',
				transportationTypeSid: '',
				serviceAdvisorSid: '',
				selectedService: '',
				opcode: '',
				customerVehicleId: this.selectedVehicle?.customer_vehicle_id || 0,
			},
			integrationAppointmentSlots: [],
			dateRange: [],
			serviceAdvisorOffset: 0,
			serviceAdvisorLimit: 100,
			expandSlots: false,
			hasSearched: false,
			isLoading: false,
			minimumDate: new Date().toISOString().split('T')[0],
			rules: {
				required: v => !!v || 'Required.',
			},
			showVehicleForm: false,
			showMoreServiceAdvisors: false,
			integrationsNeedingServiceAdvisor: [
				"tekion",
			],
			integrationsUsingServiceAdvisor: [
				"tekion",
				"xtime",
			],
			integrationsNeedingTransportationType: [
				"tekion",
			],
			integrationsUsingTransportationType: [
				"tekion",
				"xtime",
			],
			integrationsNeedingServiceShop: [
				"tekion",
			],
			integrationsUsingServiceShop: [
				"tekion",
			],
			integrationsNeedingServiceMenu: [
				"xtime",
				//TODO: Add tekion
			],
			integrationsNeedingVehicleInfoForAppointmentSlots: [
				"xtime",
			],
			integrationsNeedingCustomerVehiclesBeforeAppointmentForm: [
				"xtime",
			]
		}
	},
	methods: {
		formatSubheaderDate(timestamp) {
			const date = new Date(timestamp);
			const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
			const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

			const dayName = days[date.getDay()];
			const monthName = months[date.getMonth()];
			const day = date.getDate();

			// Get ordinal suffix (1st, 2nd, 3rd, etc.)
			const suffix = (() => {
				if (day > 3 && day < 21) return 'th';
				switch (day % 10) {
					case 1: return "st";
					case 2: return "nd";
					case 3: return "rd";
					default: return "th";
				}
			})();

			return `${dayName} ${monthName} ${day}${suffix}`;
		},
		normalizeDateRange(dateRange) {
			if (!dateRange?.length) return null;
			
			let [startDate, endDate] = dateRange;
			
			// If only one date is selected, use it as both start and end
			if (dateRange.length === 1) {
				endDate = startDate;
			}
			
			// Swap dates if start is after end
			if (new Date(startDate) > new Date(endDate)) {
				[startDate, endDate] = [endDate, startDate];
			}
			
			return { startDate, endDate };
		},
		calculateDateRangeInMilliseconds(startDate, endDate) {
			return new Date(endDate).getTime() - new Date(startDate).getTime();
		},
		validateDateRangeLimit(dateRangeInMilliseconds, integration) {
			const oneWeekInMilliseconds = 7 * 24 * 60 * 60 * 1000;
			const sixDaysInMilliseconds = 6 * 24 * 60 * 60 * 1000;

			let isValid = false;
			let limitInMilliseconds = 0;
			let message = "";
			if (integration === "xtime") {
				isValid = dateRangeInMilliseconds <= sixDaysInMilliseconds;
				limitInMilliseconds = sixDaysInMilliseconds;
				message = "Cannot select more than 7 days";
			} else {
				limitInMilliseconds = oneWeekInMilliseconds;
				isValid = dateRangeInMilliseconds <= oneWeekInMilliseconds;
				message = "Cannot select more than 8 days";
			}
			
			return {
				isValid: isValid,
				message: message,
				limitInMilliseconds: limitInMilliseconds
			};
		},
		init() {
			this.initMinDate();
		},
		initMinDate() {
			// set minimum date to today with format 2016-06-15
			const today = new Date();
			const dd = String(today.getDate()).padStart(2, '0');
			const mm = String(today.getMonth() + 1).padStart(2, '0'); // January is 0!
			const yyyy = today.getFullYear();
			this.minimumDate = `${yyyy}-${mm}-${dd}`;
		},
		onServiceChange() {
			// Reset opcode when service changes
			this.formIntegrationAppointmentSlots.opcode = '';
			
			// If there's only one opcode available for this service, automatically select it
			const opcodes = this.serviceToOpcodes[this.formIntegrationAppointmentSlots.selectedService] || [];
			if (opcodes.length === 1) {
				this.formIntegrationAppointmentSlots.opcode = opcodes[0];
			}
		},
		getAvailableSlots() {
			if (this.dateRangeValidation && !this.dateRangeValidation.isValid) {
				this.showSnackbar("red", this.dateRangeValidation.message || "");
				return;
			}

			if (this.integrationsNeedingVehicleInfoForAppointmentSlots.includes(this.currentIntegration)) {
				if (!(this.integrationOptions?.vehicle_make && this.integrationOptions?.vehicle_model && this.integrationOptions?.vehicle_year)) {
					this.showSnackbar("red", "Vehicle information is required to find available slots");
					return;
				}
			}

			const { startDate, endDate } = this.dateRangeValidation.dates;

			const options = {
				integration: this.integrationOptions?.integration || "",
				organization_id: this.integrationOptions?.organization_id || 0,
				shop_sid: this.formIntegrationAppointmentSlots?.shopSid || "",
				transportation_sid: this.formIntegrationAppointmentSlots?.transportationTypeSid || "",
				opcode: this.formIntegrationAppointmentSlots?.opcode || "",
				start_date: startDate || "",
				service_advisor_sid: this.currentIntegration !== "xtime" ? this.formIntegrationAppointmentSlots?.serviceAdvisorSid : "", //TODO: Remove condition once xtime is updated
				advisor_sid: this.currentIntegration === "xtime" ? this.formIntegrationAppointmentSlots?.serviceAdvisorSid : "", //TODO: Remove this once xtime is updated
				end_date: endDate || startDate || "", // if end date is not set, set it to start date
				vehicle_make: this.integrationOptions?.vehicle_make || "",
				vehicle_model: this.integrationOptions?.vehicle_model || "",
				vehicle_year: this.integrationOptions?.vehicle_year || "",
			};

			this.isLoading = true;
			this.hasSearched = true;

			GetIntegrationAppointmentSlots(options).then((response) => {
				if (response?.Data?.integration_appointment_slots?.length > 0) {
					// Process the data to group by dates
					this.integrationAppointmentSlots = this.groupSlotsByDate(response.Data.integration_appointment_slots);
					this.isLoading = false;
				} else {
					this.integrationAppointmentSlots = [];
					this.isLoading = false;
					this.showSnackbar("red", "No available slots found please adjust your search criteria");
				}
			});

			// add class to picker-wrapper of found-slots
			const pickerWrapper = document.getElementById('picker-wrapper');
			if (pickerWrapper) {
				pickerWrapper.classList.add('slots-available');
			}
		},
		groupSlotsByDate(appointmentSlots) {
			const groupedSlots = {};

			appointmentSlots.forEach(slot => {
				if (!(slot.friendly_start_time?.length > 0)) {
					return;
				}

				// Friendly start time with time zone = "Mon 01/02/2006 at 03:04 PM UTC" format
				const dateTimeStringSplit = slot.friendly_start_time.split(' at ')
				const titleDateString     = dateTimeStringSplit[0];
				const timeString          = dateTimeStringSplit[1];

				// group times by titleDateString corresponding to start time
				if (!groupedSlots[titleDateString]) {
					groupedSlots[titleDateString] = {
						appointmentDate: titleDateString,
						times: []
					};
				}

				// Add the slot
				groupedSlots[titleDateString].times.push({
					friendlyDateTime: slot.friendly_start_time,
					appointmentTime: timeString,
					startTimeUtc: slot.start_time_utc,
				});
			});

			// return grouped objects
			return Object.values(groupedSlots);
		},
		handleToggleVehicleForm(bool) {
			this.showVehicleForm = bool;
		},
		resetForm() {
			// remove class from picker-wrapper

			const pickerWrapper = document.getElementById('picker-wrapper');
			if (pickerWrapper) {
				pickerWrapper.classList.remove('slots-available');
			}
			this.integrationAppointmentSlots = [];
			this.dateRange = [];
			this.formIntegrationAppointmentSlots.selectedService = '';
			this.formIntegrationAppointmentSlots.opcode = '';
			this.hasSearched = false;
			this.formIntegrationAppointmentSlots.customerVehicleId = '';
		},
		selectSlot(slot) {
			const formObject = {
				integrationShopSid: this.formIntegrationAppointmentSlots?.shopSid || "",
				integrationServiceAdvisorSid: this.formIntegrationAppointmentSlots?.serviceAdvisorSid || "",
				integrationTransportationTypeSid: this.formIntegrationAppointmentSlots?.transportationTypeSid || "",
				opcode: this.formIntegrationAppointmentSlots?.opcode || "",
			}

			if (this.formIntegrationAppointmentSlots?.customerVehicleId > 0) {
				this.handleVehicleSelected(this.formIntegrationAppointmentSlots.customerVehicleId);
			}

			//set slot scoped slot, slotfor object, and navigate back to either create or udpate form
			this.handleSlotSelected(slot, formObject, this.isEdit);
		},
		validSlotTimes(slot) {
			if (!slot) {
				return false;
			}
			return slot.times && slot.times.length > 0;
		},
	},
	computed: {
		customerVehicleLabel() {
			return (item) => (`${item?.vehicle_model_year || "---"} ${item?.vehicle_make || "---"} ${item?.vehicle_model || "---"}` || "---");
		},
		showServiceAdvisor() {
			if (this.availableServices?.length === 0 && this.integrationsNeedingServiceMenu.includes(this.currentIntegration)) {
				return false;
			}

			return this.integrationsUsingServiceAdvisor.includes(this.currentIntegration) && !this.showChangeCriteriaAlert;
		},
		showTransportationType() {
			if (this.availableServices?.length === 0 && this.integrationsNeedingServiceMenu.includes(this.currentIntegration)) {
				return false;
			}

			return this.integrationsUsingTransportationType.includes(this.currentIntegration) && !this.showChangeCriteriaAlert;
		},
		showServiceShop() {
			if (this.availableServices?.length === 0 && this.integrationsNeedingServiceMenu.includes(this.currentIntegration)) {
				return false;
			}

			return this.integrationsUsingServiceShop.includes(this.currentIntegration) && !this.showChangeCriteriaAlert;
		},
		isServiceShopRequired() {
			return this.integrationsNeedingServiceShop.includes(this.currentIntegration);
		},
		isTransportationTypeRequired() {
			return this.integrationsNeedingTransportationType.includes(this.currentIntegration);
		},
		isServiceAdvisorRequired() {
			return this.integrationsNeedingServiceAdvisor.includes(this.currentIntegration);
		},
		serviceShopLabel() {
			return this.isServiceShopRequired ? "Select Shop" : "Select Shop (optional)";
		},
		transportationTypeLabel() {
			return this.isTransportationTypeRequired ? "Select Transportation" : "Select Transportation (optional)";
		},
		serviceAdvisorLabel() {
			return this.isServiceAdvisorRequired ? "Service Advisor" : "Service Advisor (optional)";
		},
		showOpcodes() {
			return this.showServiceMenu && this.opcodeToServices && Object.keys(this.opcodeToServices).length > 0 && !this.showChangeCriteriaAlert;
		},
		availableServices() {
			if (!this.serviceToOpcodes) return [];
			return Object.keys(this.serviceToOpcodes).map(serviceName => ({ serviceName }));
		},
		showOpcodeSelection() {
			return this.showServiceMenu && this.formIntegrationAppointmentSlots?.selectedService && this.availableOpcodes?.length > 1 && !this.showChangeCriteriaAlert;
		},
		availableOpcodes() {
			if (!this.formIntegrationAppointmentSlots.selectedService || (this.availableServices?.length === 0)) {
				return [];
			}
			const opcodes = this.serviceToOpcodes[this.formIntegrationAppointmentSlots.selectedService] || [];
			return opcodes.map(opcode => ({ opcode }));
		},
		showServiceMenu() {
			if (this.availableServices?.length === 0) {
				return false;
			}

			return this.integrationsNeedingServiceMenu.includes(this.currentIntegration) && !this.showChangeCriteriaAlert;
		},
		showNotFound() {
			return this.hasSearched && this.integrationAppointmentSlots?.length === 0;
		},
		showVehicleFormBeforeAppointmentDateSelection() {
			return this.currentIntegration === 'xtime';
		},
		validCampaignCustomer() {
			return this.currentCampaignCustomer?.campaign_customer_id > 0;
		},
		validIntegrationFormResourceFields() {
			switch (this.currentIntegration) {
				case "tekion":
					return this.integrationShops?.length > 0 && this.integrationServiceAdvisors?.length > 0 && this.integrationTransportationTypes?.length > 0;
				case "xtime":
					return this.validCustomerVehicles && this.integrationServiceAdvisors?.length > 0 && this.integrationTransportationTypes?.length > 0 && this.availableServices?.length > 0;
				default:
					return false;
			}
		},
		validIntegrationFormResourcesSelected() {
			let baseValidation = false;
			
			switch (this.currentIntegration) {
				case "tekion":
					// Check required fields based on integration requirements
					const shopRequired = this.isServiceShopRequired;
					const transportationRequired = this.isTransportationTypeRequired;
					const serviceAdvisorRequired = this.isServiceAdvisorRequired;
					
					baseValidation = true;
					
					// Validate shop if required
					if (shopRequired && (!this.formIntegrationAppointmentSlots?.shopSid || this.formIntegrationAppointmentSlots.shopSid.length === 0)) {
						baseValidation = false;
					}
					
					// Validate transportation type if required
					if (transportationRequired && (!this.formIntegrationAppointmentSlots?.transportationTypeSid || this.formIntegrationAppointmentSlots.transportationTypeSid.length === 0)) {
						baseValidation = false;
					}
					
					// Validate service advisor if required
					if (serviceAdvisorRequired && (!this.formIntegrationAppointmentSlots?.serviceAdvisorSid || this.formIntegrationAppointmentSlots.serviceAdvisorSid.length === 0)) {
						baseValidation = false;
					}
					break;
				case "xtime":
					// For xtime, only service advisor and transportation type are required
					const xtimeTransportationRequired = this.isTransportationTypeRequired;
					const xtimeServiceAdvisorRequired = this.isServiceAdvisorRequired;
					
					baseValidation = true;
					
					// Validate transportation type if required
					if (xtimeTransportationRequired && (!this.formIntegrationAppointmentSlots?.transportationTypeSid || this.formIntegrationAppointmentSlots.transportationTypeSid.length === 0)) {
						baseValidation = false;
					}
					
					// Validate service advisor if required
					if (xtimeServiceAdvisorRequired && (!this.formIntegrationAppointmentSlots?.serviceAdvisorSid || this.formIntegrationAppointmentSlots.serviceAdvisorSid.length === 0)) {
						baseValidation = false;
					}
					break;
				default:
					baseValidation = false;
			}

			// If service menu is required, validate service and opcode selection
			if (this.showServiceMenu) {
				const serviceSelected = this.formIntegrationAppointmentSlots?.selectedService?.length > 0;
				const opcodeSelected = this.formIntegrationAppointmentSlots?.opcode?.length > 0;
				
				// If there are multiple opcodes available, both service and opcode must be selected
				// If there's only one opcode, just the service needs to be selected
				if (this.showOpcodeSelection) {
					return baseValidation && serviceSelected && opcodeSelected;
				} else {
					return baseValidation && serviceSelected;
				}
			}

			return baseValidation;
		},
		disableDatePicker() {
			return (this.showChangeCriteriaAlert || (!(this.validSelectedVehicle) && !(this.validFirstCustomerVehicle))) && this.currentIntegration === 'xtime';
		},
		validFirstCustomerVehicle() {
			return this.firstCustomerVehicle?.customer_vehicle_id > 0;
		},
		validSelectedVehicle() {
			return this.selectedVehicle?.customer_vehicle_id > 0;
		},
		disabledFind() {
			return !this.validCampaignCustomer || !this.validIntegrationFormResourcesSelected || this.showChangeCriteriaAlert;
		},
		isEdit() {
			return this.scopedIntegrationAppointment?.appointment_sid?.length > 0;
		},
		dateRangeValidation() {
			if (!(this.dateRange?.length > 0)) {
				return {
					isValid: false,
					message: "Please select a date range",
					dates: null
				};
			}

			const normalizedDates = this.normalizeDateRange(this.dateRange);
			if (!normalizedDates) {
				return {
					isValid: false,
					message: "Please select a date range",
					dates: null
				};
			}

			const { startDate, endDate } = normalizedDates;
			const selectedDateRangeInMilliseconds = this.calculateDateRangeInMilliseconds(startDate, endDate);
			const validation = this.validateDateRangeLimit(selectedDateRangeInMilliseconds, this.currentIntegration);

			if (!validation.isValid) {
				return {
					isValid: false,
					message: validation.message,
					dates: null
				};
			}

			return {
				isValid: true,
				message: "Valid date range",
				dates: { startDate, endDate }
			};
		},
		dateRangeText() {
			if (!this.dateRange || this.dateRange.length !== 2) return '';
			
			const normalizedDates = this.normalizeDateRange(this.dateRange);
			if (!normalizedDates) return '';
			
			const { startDate, endDate } = normalizedDates;
			const start = new Date(startDate);
			const end = new Date(endDate);
			const days = Math.floor((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;
			return `${days} days selected`;
		},
		showCustomerVehicles() {
			return this.validCustomerVehicles && this.integrationsNeedingCustomerVehiclesBeforeAppointmentForm.includes(this.currentIntegration);
		},
		isIntegrationResourceLoading() {
			return this.isIntegrationResourceParentLoading || this.isServiceMenuLoading || this.isIntegrationServiceShopsLoading || this.isIntegrationTransportationTypesLoading || this.isIntegrationServiceAdvisorsLoading;
		},
		showChangeCriteriaAlert() {
			return !this.isIntegrationResourceLoading && !this.validIntegrationFormResourceFields && !this.isChangeParamsForIntegrationResourceErrorAllowed;
		},
		showCreateVehicleForm() {
			return !this.isIntegrationResourceLoading && !this.validIntegrationFormResourceFields;
		},
		validCustomerVehicles() {
			return this.customerVehicles?.length > 0;
		},
	},
	watch: {
		dateRange: {
			handler(newRange) {
				if (!newRange?.length) return;

				const normalizedDates = this.normalizeDateRange(newRange);
				if (!normalizedDates) return;

				const { startDate, endDate } = normalizedDates;
				const dateDiff = this.calculateDateRangeInMilliseconds(startDate, endDate);
				const validation = this.validateDateRangeLimit(dateDiff, this.currentIntegration);

				if (!validation.isValid) {
					this.showSnackbar("red", validation.message);
				}
			},
			deep: true
		},
		selectedVehicle: {
			handler(newValue) {
				if (newValue) {
					this.handleVehicleChange(newValue.customer_vehicle_id);
				}
			},
			immediate: true,
			deep: true,
		},
	},
	components: {
		VehicleCreate,
	},
	props: {
		currentCampaignCustomer: {
			type: Object,
			required: true,
		},
			currentIntegration: String,
			customerVehicles: Array,
			displayCreateView: Function,
			displayDetailsView: Function,
			displayRescheduleView: Function,
			handleAddCustomerVehicle: Function,
			handleSlotSelected: Function,
			handleVehicleChange: Function,
			handleVehicleSelected: Function,
			firstCustomerVehicle: Object,
			integrationCustomer: Object,
			integrationOptions: Object,
			integrationServiceAdvisors: Array,
			integrationShops: Array,
			integrationTransportationTypes: Array,
			isChangeParamsForIntegrationResourceErrorAllowed: Boolean,
			isIntegrationServiceAdvisorsLoading: Boolean,
			isIntegrationServiceShopsLoading: Boolean,
			isIntegrationTransportationTypesLoading: Boolean,
			isServiceMenuLoading: Boolean,
			opcodeToServices: Object,
			scopedIntegrationAppointment: Object,
			selectedVehicle: Object,
			serviceToOpcodes: Object,
			setIntegrationError: Function,
			setSelectedVehicle: Function,
			showSnackbar: Function,
			vehicleInfo: Object,
	},
	created() {
		this.init();
	},
	destroyed() {
	},
})
</script>

<style lang="scss" scoped>
#app {
	#picker-wrapper {
		width: 100%;

		// if slots available then scale picker-wrapper to 50%
		#date-selector {
			transition: transform 0.5s ease-in-out;
		}

		.reset-cover {
			display: none;
			transition: background-color 0.5s ease-in-out;
		}

		&.slots-available {
			display: flex;

			#date-selector {
				flex-basis: 33.33%;
				transform: scale(0.5);
				transform-origin: top left;
				position: relative;

				>div.row {
					position: absolute;
					top: 20%;
					width: 230%;
				}
			}

			#slot-picker {
				flex-basis: 55.66%;
				padding: 20px;
				margin-left: 10%;
				max-height: 400px;
				overflow-y: scroll;

				&::-webkit-scrollbar {
					display: none;
				}

				-ms-overflow-style: none;
				scrollbar-width: none;
			}

			.reset-cover {
				position: absolute;
				top: 10%;
				bottom: 0;
				height: 100%;
				background-color: rgba(15, 15, 15, 0);
				z-index: 10;
				justify-content: center;
				align-items: center;
				display: flex;
				padding: 10px;
				margin-left: -5px;
				max-width: 40%;

				.reset-details {
					opacity: 0;
					transition: opacity 0.5s ease-in-out;
					color: var(--white);
				}
			}

			.reset-cover:hover {
				background-color: rgba(15, 15, 15, 0.9);

				.reset-details {
					opacity: 1;
				}
			}
		}

		.slot-item {
			padding: 10px;
			border: 2px solid #010101;
			margin-bottom: 10px;
			cursor: pointer;
			background-color: var(--lt-green);
			transition: background-color 0.5s ease-in-out;

			&:hover {
				background-color: var(--success);
			}

			p {
				margin: 0;
				font-size: 0.8rem;
			}

			&.unavailable {
				background-color: var(--lt-red);

				&:hover {
					background-color: var(--error);
				}

				cursor: not-allowed;
			}
		}
	}
}

.vin-text {
	color: #999;
	font-size: 0.85em;
	font-weight: normal;
}

.vehicle-info {
	color: var(--volie-blue);
	font-weight: bold;
}

.date-picker-container {
	position: relative;
	transition: opacity 0.3s ease-in-out;
	
	&.disabled {
		opacity: 0.5;
		pointer-events: none;
		
		&::after {
			content: '';
			position: absolute;
			top: 0;
			left: 0;
			right: 0;
			bottom: 0;
			background-color: rgba(0, 0, 0, 0.1);
			z-index: 1;
			border-radius: 4px;
		}
	}
}
</style>